import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "@/components/base/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <SEOHead title="分类未找到" description="抱歉，您访问的分类不存在。" />
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">
              {t("back_to_home")}
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryMeta = {
    yanghu: {
      title: "猫咪养护——科学养猫知识大全 | 猫咪社区",
      description: "全方位的猫咪养护指南，涵盖日常护理、用品推荐、季节养护等实用知识，帮助铲屎官科学照顾猫咪的每一天。",
    },
    jiankang: {
      title: "猫咪健康问答——疾病预防与医疗指南 | 猫咪社区",
      description: "专业的猫咪健康问答社区，提供疾病预防、症状识别、体检指南和兽医建议，守护猫咪健康成长。",
    },
    mengtu: {
      title: "萌猫图片分享——晒出你家主子的可爱瞬间 | 猫咪社区",
      description: "猫咪社区萌猫图片分享板块，海量高清萌猫图片，记录猫咪成长的点滴美好，与铲屎官们一起分享快乐。",
    },
    lingyang: {
      title: "猫咪领养信息——给流浪猫一个温暖的家 | 猫咪社区",
      description: "猫咪领养信息发布平台，汇聚全国猫咪领养资讯，倡导领养代替购买，帮助更多流浪猫咪找到温暖的家。",
    },
    xingwei: {
      title: "猫咪行为解读——读懂主子的每一个小动作 | 猫咪社区",
      description: "深度解读猫咪行为背后的秘密，从踩奶到呼噜，从炸毛到蹭人，帮你成为最懂猫咪的铲屎官。",
    },
    yinshi: {
      title: "猫咪饮食营养——科学喂养从选粮开始 | 猫咪社区",
      description: "猫咪饮食营养全方位指南，从商业猫粮到自制猫饭，从营养搭配到禁忌食物，让你的猫咪吃得健康又美味。",
    },
  };

  const meta = categoryMeta[slug as keyof typeof categoryMeta] || {
    title: `${category.name} | 猫咪社区`,
    description: category.description,
  };

  return (
    <>
      <SEOHead
        title={meta.title}
        description={meta.description}
        keywords={`猫咪社区,${category.name},猫咪养护交流,铲屎官社区`}
        canonicalUrl={`https://catcommunity.cn/category/${slug}`}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: t("breadcrumb_home"), href: "/" },
              { label: category.name },
            ]}
          />

          <div className="px-4 md:px-6 pb-14 md:pb-20">
            <div className="mb-10">
              <div className="flex items-center gap-4 mb-4">
                <span className={`w-12 h-12 flex items-center justify-center rounded-full ${
                  category.color === "accent"
                    ? "bg-accent-100 text-accent-600"
                    : "bg-primary-100 text-primary-600"
                }`}>
                  <i className={`${category.icon} text-xl`}></i>
                </span>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900">{category.name}</h1>
                  <p className="text-sm text-foreground-500 mt-1">{category.description}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-400">
                共 {categoryArticles.length} 篇文章
              </p>
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all duration-300 hover:bg-background-200/50"
                  >
                    <div className="relative h-48 md:h-52 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base font-medium text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 line-clamp-2 mb-4 leading-relaxed">
                        {article.summary}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <div className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-eye-line"></i>
                            </span>
                            {article.readCount > 1000 ? `${(article.readCount / 1000).toFixed(1)}k` : article.readCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-heart-line"></i>
                            </span>
                            {article.likeCount}
                          </span>
                        </div>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}